import { localized, msg, str } from '@lit/localize';
import { html, LitElement, nothing, type TemplateResult } from 'lit';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@spectrum-web-components/popover/sp-popover.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-send.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-checkmark.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-close.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../../../api';
import type {
  WorkflowAgentRun,
  WorkflowAgentRunEvent,
  WorkflowAgentRunSummary,
} from '../../../../api/workflow-agent';
import { formatDate } from '../../../../utils/format';
import type { Step, StepContent } from './types';
import { workflowAgentStyles } from './workflow-agent.styles';

type ApprovalState = {
  runId: string;
  planSummary: string;
  operations: unknown[];
};

type View = 'list' | 'detail';

@customElement('workflow-agent')
@localized()
export class WorkflowAgent extends LitElement {
  @property() accessor goal = '';
  @property({ type: Number }) accessor workflowId: number | undefined =
    undefined;
  @state() accessor running = false;
  @state() accessor steps: Step[] = [];
  @state() accessor pendingApproval: ApprovalState | null = null;
  @state() accessor view: View = 'list';
  @state() accessor runs: WorkflowAgentRunSummary[] = [];
  @state() accessor loadingRuns = false;
  @state() accessor activeRunId: string | null = null;
  /** Root run id of the currently-open thread (for continuations). */
  @state() accessor threadRootId: string | null = null;
  /** Run id awaiting inline delete confirmation, if any. */
  @state() accessor pendingDeleteRunId: string | null = null;
  /**
   * If set, every plan in this thread that hits `waiting_approval` is auto-
   * approved without showing the banner. Cleared on new conversation, thread
   * switch, or rejection. Frontend-only — does not persist across reloads.
   */
  @state() accessor autoApproveThreadId: string | null = null;

  private streamAbort: AbortController | null = null;

  static styles = workflowAgentStyles;

  willUpdate(changed: Map<string | number | symbol, unknown>) {
    if (changed.has('workflowId')) {
      this.steps = [];
      this.activeRunId = null;
      this.threadRootId = null;
      this.pendingApproval = null;
      this.autoApproveThreadId = null;
      this.view = 'list';
      void this.loadRunsAndOpenLatest();
    }
  }

  private async loadRunsAndOpenLatest(): Promise<void> {
    await this.loadRuns();
    const latest = this.runs[0];
    if (latest) await this.openExistingRun(latest.id);
  }

  render() {
    if (this.view === 'list') return this.renderList();
    return this.renderDetail();
  }

  private renderList(): TemplateResult {
    return html`
      <div class="conversations-header">
        <span class="conversations-title">${msg('Conversations')}</span>
        <sp-action-button
          quiet
          size="s"
          label=${msg('New conversation')}
          @click=${() => this.startNewConversation()}
        >
          <sp-icon-add slot="icon"></sp-icon-add>
          ${msg('New')}
        </sp-action-button>
      </div>
      <div class="conversations-list">
        ${
          this.loadingRuns
            ? html`<div class="empty-state">${msg('Loading...')}</div>`
            : this.runs.length === 0
              ? html`<div class="empty-state">${msg('No conversations yet. Start one to ask the agent about this workflow.')}</div>`
              : this.runs.map((r) => this.renderRunRow(r))
        }
      </div>
    `;
  }

  private renderRunRow(run: WorkflowAgentRunSummary): TemplateResult {
    const subtitle =
      run.status === 'waiting_approval'
        ? msg('Awaiting approval')
        : run.status === 'rejected'
          ? msg('Rejected')
          : run.status === 'failed'
            ? (run.failureReason ?? msg('Failed'))
            : (run.planSummary ?? '');
    return html`
      <div class="conversation-row status-${run.status}">
        <button
          type="button"
          class="conversation-row-main"
          @click=${() => this.openExistingRun(run.id)}
        >
          <span class="conversation-goal">${run.goal}</span>
          ${
            subtitle
              ? html`<span class="conversation-subtitle">${subtitle}</span>`
              : nothing
          }
          <span class="conversation-meta">
            <span class="conversation-status">
              ${this.formatRunStatus(run.status)}
            </span>
            <span class="conversation-time">${formatRelative(run.createdAt)}</span>
          </span>
        </button>
        <sp-action-button
          quiet
          size="s"
          label=${this.pendingDeleteRunId === run.id ? msg('Confirm delete') : msg('Delete conversation')}
          class="conversation-delete ${this.pendingDeleteRunId === run.id ? 'confirm' : ''}"
          @click=${(e: Event) => this.handleDeleteRun(e, run.id)}
        >
          <sp-icon-delete slot="icon"></sp-icon-delete>
        </sp-action-button>
      </div>
    `;
  }

  private formatRunStatus(status: WorkflowAgentRunSummary['status']): string {
    switch (status) {
      case 'queued':
        return msg('Queued');
      case 'running':
        return msg('Running');
      case 'waiting_approval':
        return msg('Awaiting approval');
      case 'completed':
        return msg('Completed');
      case 'failed':
        return msg('Failed');
      case 'cancelled':
        return msg('Cancelled');
      case 'rejected':
        return msg('Rejected');
    }
  }

  private renderDetail(): TemplateResult {
    const hasContent = this.steps.length > 0 || this.running;

    return html`
      <div class="detail-header">
        <sp-action-button
          quiet
          size="s"
          label=${msg('Back to conversations')}
          @click=${() => this.goBackToList()}
        >
          <sp-icon-chevron-left slot="icon"></sp-icon-chevron-left>
          ${msg('Back')}
        </sp-action-button>
      </div>
      <div class="steps">
        ${
          !hasContent
            ? html`<div class="empty-state">${msg('Ask me to explain, change, or build this workflow.')}</div>`
            : nothing
        }
        ${this.steps.map((s) => this.renderStep(s))}
        ${
          this.running
            ? html`<div class="assistant">
              <div class="thinking" role="status" aria-label=${msg('Thinking')}>
                <div class="typing" aria-hidden="true">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </div>
              </div>
            </div>`
            : nothing
        }
      </div>

      ${
        this.pendingApproval
          ? this.renderApprovalBanner(this.pendingApproval)
          : html`<div class="goal-wrapper">
            <sp-textfield
              multiline
              grows
              .value=${this.goal}
              placeholder=${msg('Ask anything about this workflow...')}
              ?disabled=${this.running}
              @input=${(e: Event) => {
                this.goal = (e.target as unknown as { value: string }).value;
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  this.goal !== '' &&
                  !this.running
                ) {
                  e.preventDefault();
                  this.runAgent(this.goal);
                }
              }}
            ></sp-textfield>
            ${
              this.running && this.activeRunId
                ? html`
                  <sp-action-button
                    quiet
                    label=${msg('Cancel')}
                    @click=${() => this.handleCancel()}
                  >
                    <sp-icon-close slot="icon"></sp-icon-close>
                  </sp-action-button>
                `
                : this.goal !== ''
                  ? html`
                    <sp-action-button
                      quiet
                      label=${msg('Run')}
                      @click=${() => this.runAgent(this.goal)}
                    >
                      <sp-icon-send slot="icon"></sp-icon-send>
                    </sp-action-button>
                  `
                  : nothing
            }
          </div>`
      }
    `;
  }

  private renderApprovalBanner(approval: ApprovalState): TemplateResult {
    return html`
      <div class="approval-banner">
        <span class="approval-title">${msg('Apply plan?')}</span>
        ${
          approval.planSummary
            ? html`<span class="approval-plan-text">${approval.planSummary}</span>`
            : nothing
        }
        <ul class="approval-ops">
          ${approval.operations.map(
            (op) => html`<li>${describeOperation(op)}</li>`
          )}
        </ul>
        <div class="approval-actions">
          <overlay-trigger placement="top" triggered-by="longpress">
            <sp-action-button
              slot="trigger"
              hold-affordance
              variant="accent"
              treatment="fill"
              @click=${() => this.handleApprove(approval.runId)}
            >
              <sp-icon-checkmark slot="icon"></sp-icon-checkmark>
              ${msg('Apply')}
            </sp-action-button>
            <sp-popover slot="longpress-content" tip>
              <sp-menu
                @change=${(e: Event) => {
                  const menu = e.target as HTMLElement & {
                    selected?: string[];
                    value?: string;
                  };
                  const value = menu.value;
                  if (value === 'allow-all') {
                    void this.handleApproveAll(approval.runId);
                  }
                }}
              >
                <sp-menu-item value="allow-all">
                  ${msg('Allow all in this session')}
                </sp-menu-item>
              </sp-menu>
            </sp-popover>
          </overlay-trigger>
          <sp-button
            size="s"
            variant="secondary"
            @click=${() => this.handleReject(approval.runId)}
          >
            <sp-icon-close slot="icon"></sp-icon-close>
            ${msg('Reject')}
          </sp-button>
        </div>
      </div>
    `;
  }

  updated() {
    // Only auto-scroll while the agent is actively running
    if (!this.running) return;
    const stepsContainer =
      this.renderRoot.querySelector<HTMLDivElement>('.steps');
    if (stepsContainer) {
      stepsContainer.scrollTop = stepsContainer.scrollHeight;
    }
  }

  private renderStep(step: Step) {
    let content: TemplateResult | undefined;
    let reasoning: TemplateResult | undefined;
    if (step.role === 'tool') {
      content = html`<span class="content">${step.name}</span>`;
    } else {
      if (step.content) {
        content = html`<span class="content">${this.resolveStepContent(step)}</span>`;
      }
    }
    if (step.reasoning) {
      reasoning = html`<span class="reasoning">${step.reasoning}</span>`;
    }
    return html`<div class="${step.role}">${content} ${reasoning}</div>`;
  }

  private resolveStepContent(step: Step): string {
    return typeof step.content === 'function' ? step.content() : step.content;
  }

  private async loadRuns(): Promise<void> {
    const wfId = this.workflowId;
    if (wfId === undefined) {
      this.runs = [];
      return;
    }
    this.loadingRuns = true;
    try {
      const result = await api.workflowAgent.listRuns(wfId);
      this.runs = result.runs;
    } catch {
      this.runs = [];
    } finally {
      this.loadingRuns = false;
    }
  }

  private startNewConversation(): void {
    this.streamAbort?.abort();
    this.streamAbort = null;
    this.steps = [];
    this.goal = '';
    this.pendingApproval = null;
    this.activeRunId = null;
    this.threadRootId = null;
    this.autoApproveThreadId = null;
    this.running = false;
    this.view = 'detail';
  }

  private goBackToList(): void {
    this.streamAbort?.abort();
    this.streamAbort = null;
    this.running = false;
    this.view = 'list';
    void this.loadRuns();
  }

  private async openExistingRun(runId: string): Promise<void> {
    this.streamAbort?.abort();
    this.streamAbort = null;
    this.steps = [];
    this.pendingApproval = null;
    this.autoApproveThreadId = null;
    this.view = 'detail';
    this.running = false;

    try {
      const thread = await api.workflowAgent.getThread(runId);
      const first = thread[0];
      const last = thread.at(-1);
      if (!first || !last) return;
      this.threadRootId = first.id;
      this.activeRunId = last.id;
      const lastSeq = this.rebuildStepsFromThread(thread);

      if (last.status === 'waiting_approval') {
        this.pendingApproval = {
          runId: last.id,
          planSummary: last.planSummary ?? '',
          operations: Array.isArray(last.planOperations)
            ? last.planOperations
            : [],
        };
      }

      // Open the live stream for any non-terminal status, including
      // waiting_approval — the stream stays open across approval gates and
      // delivers post-approval events on the same connection.
      const nonTerminal =
        last.status === 'queued' ||
        last.status === 'running' ||
        last.status === 'waiting_approval';
      if (nonTerminal) {
        this.running = last.status !== 'waiting_approval';
        try {
          await this.streamRun(last.id, lastSeq);
        } finally {
          this.running = false;
          void this.loadRuns();
        }
      }
    } catch (e) {
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg(str`Failed to load run: ${String(e)}`),
        },
      ];
    }
  }

  /** Rebuilds the step log from a thread; returns max seq of the last run. */
  private rebuildStepsFromThread(thread: WorkflowAgentRun[]): number {
    const steps: Step[] = [];
    let lastRunMaxSeq = 0;
    for (const [i, run] of thread.entries()) {
      if (!isAutoSpawnedRun(run)) {
        steps.push({ role: 'user', content: run.goal });
      }
      let maxSeq = 0;
      for (const event of run.events) {
        if (event.seq > maxSeq) maxSeq = event.seq;
        const content = this.eventToStepContent(event);
        if (content) steps.push({ role: 'assistant', content });
      }
      if (i === thread.length - 1) lastRunMaxSeq = maxSeq;
    }
    this.steps = steps;
    return lastRunMaxSeq;
  }

  refreshWorkflowsList() {
    this.dispatchEvent(
      new CustomEvent('refresh-workflows', { bubbles: true, composed: true })
    );
  }

  async arrangeLayout() {
    this.dispatchEvent(
      new CustomEvent('arrange-layout', { bubbles: true, composed: true })
    );
  }

  private async handleApprove(runId: string, autoApproveAll = false) {
    this.pendingApproval = null;
    this.running = true;
    try {
      await api.workflowAgent.approve(runId, { autoApproveAll });
      // Do not reopen the stream — the existing SSE connection (opened when
      // the run was first observed) stays alive across approval gates and
      // will deliver post-approval events on the same connection.
    } catch (e) {
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg(str`Approval failed: ${String(e)}`),
        },
      ];
      this.running = false;
    }
  }

  private async handleApproveAll(runId: string) {
    this.autoApproveThreadId = this.threadRootId;
    await this.handleApprove(runId, true);
  }

  private async handleDeleteRun(e: Event, runId: string): Promise<void> {
    e.stopPropagation();
    if (this.pendingDeleteRunId !== runId) {
      this.pendingDeleteRunId = runId;
      return;
    }
    this.pendingDeleteRunId = null;
    try {
      await api.workflowAgent.deleteRun(runId);
      this.runs = this.runs.filter((r) => r.id !== runId);
      if (this.activeRunId === runId || this.threadRootId === runId) {
        this.streamAbort?.abort();
        this.streamAbort = null;
        this.activeRunId = null;
        this.threadRootId = null;
        this.steps = [];
        this.pendingApproval = null;
        this.running = false;
        this.view = 'list';
      }
    } catch (err) {
      console.error('Failed to delete conversation', err);
    }
  }

  private async handleCancel(): Promise<void> {
    const runId = this.activeRunId;
    if (!runId) return;
    try {
      await api.workflowAgent.cancel(runId);
    } catch (e) {
      // 409 = run already finished between click and request; harmless.
      const errorMessage = String(e);
      if (errorMessage.includes('409')) return;
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg(str`Cancel failed: ${errorMessage}`),
        },
      ];
    }
  }

  private async handleReject(runId: string) {
    this.pendingApproval = null;
    this.autoApproveThreadId = null;
    try {
      await api.workflowAgent.reject(runId, 'Rejected by user');
      this.steps = [
        ...this.steps,
        { role: 'assistant', content: () => msg('Changes discarded.') },
      ];
    } catch (e) {
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg(str`Rejection failed: ${String(e)}`),
        },
      ];
    } finally {
      void this.loadRuns();
    }
  }

  /**
   * Subscribe to a run's SSE stream. The stream follows the entire thread
   * (parent + spawned children) and stays open across approval gates: when
   * `run.approval_required` arrives we set `pendingApproval` (or auto-approve)
   * but keep the connection live so post-approval events flow on the same
   * stream — no reopening, no replay-skipping needed.
   *
   * Resolves when the stream closes (every tracked run terminal, or abort).
   */
  private async streamRun(runId: string, afterSeq = 0): Promise<void> {
    this.streamAbort?.abort();
    const abort = new AbortController();
    this.streamAbort = abort;

    const seenKeys = new Set<string>();
    const seenRunIds = new Set<string>([runId]);

    const handleEvent = (event: WorkflowAgentRunEvent) => {
      const eventRunId = event.runId ?? runId;
      const key = `${eventRunId}:${event.seq}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);

      if (event.type === 'run.approval_required') {
        this.onApprovalRequired(eventRunId, event.payload);
        return;
      }

      // First event from a child run: track it as the new "active" run so
      // Cancel targets the live one (the parent is already terminal by the
      // time a child is producing events). Render the goal as a user bubble
      // only for user-typed continuations — auto-spawned children carry an
      // `autoSpawned` flag and should not surface their internal goal text.
      if (!seenRunIds.has(eventRunId)) {
        seenRunIds.add(eventRunId);
        this.activeRunId = eventRunId;
        if (event.type === 'run.started' && !event.payload.autoSpawned) {
          const goal = event.payload.goal;
          if (typeof goal === 'string') {
            this.steps = [...this.steps, { role: 'user', content: goal }];
          }
        }
      }

      if (event.type === 'run.completed') {
        const n = event.payload.operationCount;
        if (typeof n === 'number' && n > 0) {
          this.refreshWorkflowsList();
          void this.arrangeLayout();
        }
      }

      const stepContent = this.eventToStepContent(event);
      if (stepContent) {
        this.steps = [
          ...this.steps,
          { role: 'assistant', content: stepContent },
        ];
      }
    };

    try {
      await api.workflowAgent.streamRun(runId, {
        onEvent: handleEvent,
        signal: abort.signal,
        afterSeq,
      });
    } catch (e) {
      if (!abort.signal.aborted) {
        this.steps = [
          ...this.steps,
          {
            role: 'assistant',
            content: () => msg(str`Stream error: ${String(e)}`),
          },
        ];
      }
    } finally {
      if (this.streamAbort === abort) this.streamAbort = null;
    }
  }

  /**
   * Called when any tracked run in the live stream emits
   * `run.approval_required`. Either auto-approves (when allow-all is on for
   * this thread) or shows the approval banner. The SSE stays open either way.
   */
  private async onApprovalRequired(
    runId: string,
    payload: WorkflowAgentRunEvent['payload']
  ): Promise<void> {
    if (
      this.autoApproveThreadId &&
      this.autoApproveThreadId === this.threadRootId
    ) {
      try {
        await api.workflowAgent.approve(runId, { autoApproveAll: true });
      } catch (e) {
        this.steps = [
          ...this.steps,
          {
            role: 'assistant',
            content: () => msg(str`Approval failed: ${String(e)}`),
          },
        ];
      }
      return;
    }
    this.pendingApproval = {
      runId,
      planSummary:
        typeof payload.planSummary === 'string' ? payload.planSummary : '',
      operations: Array.isArray(payload.operations) ? payload.operations : [],
    };
  }

  private eventToStepContent(event: WorkflowAgentRunEvent): StepContent | null {
    switch (event.type) {
      case 'run.step':
        return null;
      case 'stage.discover.completed':
        return null;
      case 'stage.plan.completed': {
        if (typeof event.payload.message === 'string') {
          return event.payload.message;
        }
        if (typeof event.payload.summary === 'string') {
          return event.payload.summary;
        }
        return null;
      }
      case 'stage.validate.completed':
        return null;
      case 'run.approval_required':
        return null;
      case 'apply.task_created': {
        const name = describeTask(event.payload);
        return () => (name ? msg(str`Added task: ${name}`) : msg('Added task'));
      }
      case 'apply.task_updated': {
        const name = describeTask(event.payload);
        return () =>
          name ? msg(str`Updated task: ${name}`) : msg('Updated task');
      }
      case 'apply.connection_created': {
        const edge = describeConnection(event.payload);
        return () =>
          edge ? msg(str`Connected ${edge}`) : msg('Connected tasks');
      }
      case 'apply.connection_deleted':
        return () => msg('Removed connection');
      case 'apply.workflow_run.started':
        return () => msg('Running the workflow...');
      case 'apply.workflow_run.task_completed': {
        const name = describeTask({
          taskType: event.payload.taskType,
          label: event.payload.label,
        });
        return () =>
          name ? msg(str`Task completed: ${name}`) : msg('Task completed');
      }
      case 'apply.workflow_run.task_failed': {
        const name = describeTask({
          taskType: event.payload.taskType,
          label: event.payload.label,
        });
        const err =
          typeof event.payload.error === 'string' ? event.payload.error : '';
        return () => {
          const prefix = name
            ? msg(str`Task failed: ${name}`)
            : msg('Task failed');
          return err ? `${prefix} — ${err}` : prefix;
        };
      }
      case 'apply.workflow_run.completed': {
        const status = String(event.payload.status ?? 'success');
        // Successful runs get a single confirmation line; failures fall
        // through to `apply.workflow_run.message` (a per-task error list)
        // and the LLM's natural-language reply, so we suppress this generic
        // line on non-success to avoid three near-identical messages.
        if (status === 'success')
          return () => msg('Workflow run finished successfully');
        if (status === 'timeout') return () => msg('Workflow run timed out');
        return null;
      }
      case 'apply.workflow_run.message': {
        return typeof event.payload.message === 'string'
          ? event.payload.message
          : null;
      }
      case 'run.next_step_spawned':
        return null;
      case 'run.iteration_capped':
        return () => msg('Stopped iterating (reached the per-thread cap).');
      case 'run.completed': {
        const n = event.payload.operationCount;
        if (typeof n === 'number' && n > 0)
          return () =>
            n === 1
              ? msg('Done - 1 change applied')
              : msg(str`Done - ${n} changes applied`);
        return null;
      }
      case 'run.failed': {
        const reason = String(event.payload.reason ?? '');
        // Drop consecutive duplicate failure messages. The orchestrator's
        // auto-spawned follow-up after a workflow-execution failure can
        // surface the same model refusal twice (parent run.failed +
        // follow-up child run.failed); collapsing them keeps the chat tidy.
        const last = this.steps[this.steps.length - 1];
        if (
          last?.role === 'assistant' &&
          this.resolveStepContent(last) ===
            (reason
              ? msg(str`Failed: ${reason}`)
              : msg(str`Failed: ${msg('Unknown error')}`))
        ) {
          return null;
        }
        return () =>
          reason
            ? msg(str`Failed: ${reason}`)
            : msg(str`Failed: ${msg('Unknown error')}`);
      }
      case 'run.cancelled':
        return () => msg('Cancelled.');
      case 'run.rejected':
        return null;
      default:
        return null;
    }
  }

  private async runAgent(goal: string) {
    if (this.running) return;
    this.running = true;
    this.pendingApproval = null;

    if (this.workflowId === undefined) {
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg('Please open a workflow before using the agent.'),
        },
      ];
      this.running = false;
      return;
    }

    this.steps = [...this.steps, { role: 'user', content: goal }];
    this.goal = '';

    const parentRunId = this.activeRunId;

    try {
      const created = await api.workflowAgent.createRun({
        workflowId: this.workflowId,
        goal,
        parentRunId,
      });
      this.activeRunId = created.runId;
      if (!this.threadRootId) this.threadRootId = created.runId;

      await this.streamRun(created.runId);
    } catch (e) {
      this.steps = [
        ...this.steps,
        {
          role: 'assistant',
          content: () => msg(str`Something went wrong: ${String(e)}`),
        },
      ];
    } finally {
      this.running = false;
      void this.loadRuns();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.streamAbort?.abort();
    this.streamAbort = null;
  }
}

function isAutoSpawnedRun(run: WorkflowAgentRun): boolean {
  const started = run.events.find((e) => e.type === 'run.started');
  return started?.payload?.autoSpawned === true;
}

function describeOperation(op: unknown): string {
  const o = op as Record<string, unknown>;
  if (o.type === 'create_task') {
    const taskType = String(o.taskType ?? '');
    const label = o.label ? ` "${String(o.label)}"` : '';
    return msg(str`Add ${taskType} task${label}`);
  }
  if (o.type === 'update_task') return msg('Update task');
  if (o.type === 'create_connection') return msg('Connect tasks');
  if (o.type === 'delete_connection') return msg('Remove connection');
  if (o.type === 'execute_workflow') return msg('Run the workflow');
  return String(o.type ?? op);
}

function describeTask(payload: Record<string, unknown>): string {
  const label = typeof payload.label === 'string' ? payload.label : '';
  const type = typeof payload.taskType === 'string' ? payload.taskType : '';
  if (label && type) return `${label} (${type})`;
  return label || type || '';
}

function describeConnection(payload: Record<string, unknown>): string {
  const fromLabel =
    typeof payload.fromTaskLabel === 'string' ? payload.fromTaskLabel : '';
  const fromType =
    typeof payload.fromTaskType === 'string' ? payload.fromTaskType : '';
  const toLabel =
    typeof payload.toTaskLabel === 'string' ? payload.toTaskLabel : '';
  const toType =
    typeof payload.toTaskType === 'string' ? payload.toTaskType : '';
  const from = fromLabel || fromType;
  const to = toLabel || toType;
  if (!from || !to) return '';
  return `${from} → ${to}`;
}

function formatRelative(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffSec = Math.round((Date.now() - ts) / 1000);
  if (diffSec < 60) return msg('just now');
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return msg(str`${diffMin}m ago`);
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return msg(str`${diffHr}h ago`);
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return msg(str`${diffDay}d ago`);
  return formatDate(new Date(ts));
}

export default WorkflowAgent;
